import { LabelInfo } from "@/api/app-configuration.api";
import { getFilterByFilterName, getFilterGroup } from "@/data/filters/config";
import {
  createFilterGroup,
  filterGroupTypes,
} from "@/data/filters/filter-types";
import {
  DepartmentSizeQueryFilter,
  EventQueryFilter,
  type QueryFilter,
} from "@/types/campaign";
import { TypeOfAuditorAssistanceConvertedEnum } from "@/types/company";
import { match, P } from "ts-pattern";
import { mapAndExclude } from "./array-utils";
import { formatDate } from "./dates";

export const rangeFilterToForm = (filter?: QueryFilter) =>
  match(filter)
    .with({ operator: "Equal" }, ({ values }) => ({
      min: values[0] ? Number(values[0]) : null,
      max: values[0] ? Number(values[0]) : null,
    }))
    .with({ operator: "Between" }, ({ values }) => ({
      min: values[0] ? Number(values[0]) : null,
      max: values[1] ? Number(values[1]) : null,
    }))
    .with({ operator: "GreaterThan" }, ({ values }) => ({
      min: values[0] ? Number(values[0]) : null,
      max: null,
    }))
    .with({ operator: "LessThan" }, ({ values }) => ({
      min: null,
      max: values[0] ? Number(values[0]) : null,
    }))
    .otherwise(() => ({
      min: null,
      max: null,
    }));

type RangeValues = {
  min: number | null;
  max: number | null;
};

export const typeOfAuditorAssistanceOptions = [
  {
    title: "Ingen gennemgang (Ingen Erklæring)",
    value: TypeOfAuditorAssistanceConvertedEnum.IngenErklaering,
  },
  {
    title: "Ingen gennemgang (Andre Erklæringer)",
    value: TypeOfAuditorAssistanceConvertedEnum.AndreErklaering,
  },
  {
    title: "Simpel gennemgang (Review)",
    value: TypeOfAuditorAssistanceConvertedEnum.Review,
  },
  {
    title: "Udvidet gennemgang",
    value: TypeOfAuditorAssistanceConvertedEnum.UdvidetGennemgang,
  },
  {
    title: "Fuld revision",
    value: TypeOfAuditorAssistanceConvertedEnum.Revision,
  },
];

export function createRangeFilter(values: RangeValues) {
  return match(values)
    .with({ min: P.number, max: P.number }, ({ min, max }) => {
      if (min === max) {
        return {
          operator: "Equal" as const,
          values: [min.toString()],
        };
      }
      return {
        operator: "Between" as const,
        values: [min.toString(), max.toString()],
      };
    })
    .with({ min: P.number }, ({ min }) => ({
      operator: "GreaterThan" as const,
      values: [min.toString()],
    }))
    .with({ max: P.number }, ({ max }) => ({
      operator: "LessThan" as const,
      values: [max.toString()],
    }))
    .otherwise(() => undefined);
}

export function isLiquidCompany(filter?: QueryFilter) {
  if (!filter) return undefined;
  if (filter.filterName !== "performance-liquid-company")
    throw new Error(
      "Attempted to check for liquidity with a non-liquidity filter",
    );

  return filter.operator === "GreaterThan" && Number(filter.values[0]) >= 100;
}

export function listContainsToBoolean(filter?: QueryFilter) {
  if (!filter) return null;
  return filter.operator === "ListContains" ? true : false;
}

export function stringifyFilterValues(
  filter: QueryFilter | EventQueryFilter | DepartmentSizeQueryFilter,
): QueryFilter | EventQueryFilter | DepartmentSizeQueryFilter {
  if (filter.operator === "EventFilter") {
    return {
      ...filter,
      eventType: filter.eventType,
      timeValues: filter.timeValues
        ? stringifyValues(filter.timeValues)
        : undefined,
      numberValues: filter.numberValues
        ? stringifyValues(filter.numberValues)
        : undefined,
      stringValues: filter.stringValues
        ? stringifyValues(filter.stringValues)
        : undefined,
    };
  } else if (filter.operator === "DepartmentSize") {
    return {
      ...filter,
      groups: filter.groups.map((group) => ({
        ...group,
        values: stringifyValues(group.values),
      })),
    };
  }
  return {
    ...filter,
    values: stringifyValues(filter.values),
  };
}

export function stringifyValues(values: string[]): string[] {
  return values.map((value) => value?.toString());
}

export function createFilterPredicate(targetFilter: {
  fieldName: string;
  filterName: string;
}) {
  return (
    filter: QueryFilter | EventQueryFilter | DepartmentSizeQueryFilter,
  ) =>
    targetFilter.filterName
      ? filter.filterName === targetFilter.filterName
      : filter.fieldName === targetFilter.fieldName;
}

export function updateFilter(
  filters: (QueryFilter | EventQueryFilter | DepartmentSizeQueryFilter)[],
  targetFilter: Pick<QueryFilter, "fieldName" | "filterName">,
  newFilter:
    | Omit<QueryFilter, "fieldName" | "filterName">
    | Omit<EventQueryFilter, "fieldName" | "filterName">
    | Omit<DepartmentSizeQueryFilter, "fieldName" | "filterName">
    | undefined,
): (QueryFilter | EventQueryFilter | DepartmentSizeQueryFilter)[] {
  const filterPredicate = createFilterPredicate(targetFilter);

  if (newFilter === undefined) {
    return filters.filter((f) => !filterPredicate(f));
  }

  const filterOb: QueryFilter | EventQueryFilter | DepartmentSizeQueryFilter =
    stringifyFilterValues({
      ...newFilter,
      fieldName: targetFilter.fieldName,
      filterName: targetFilter.filterName ?? targetFilter.fieldName,
    });

  // Find the index of the existing filter (if it exists)
  const existingIndex = filters.findIndex(filterPredicate);

  if (existingIndex === -1) {
    // If the filter is new, add it to the end
    return [...filters, filterOb];
  }

  // If we're updating an existing filter, maintain its position
  const newFilters = [...filters];
  newFilters[existingIndex] = filterOb;
  return newFilters;
}

export function formatRelativeDate(values: string[], operator: string) {
  let returnValue = "";
  switch (operator) {
    case "MoreThanDaysAgo":
      returnValue += `Mere end ${values[0]} dage siden`;
      break;
    case "MoreThanWeeksAgo":
      returnValue += `Mere end ${values[0]} uger siden`;
      break;
    case "MoreThanMonthsAgo":
      returnValue += `Mere end ${values[0]} måneder siden`;
      break;
    case "MoreThanYearsAgo":
      returnValue += `Mere end ${values[0]} år siden`;
      break;
    case "LessThanDaysAgo":
      returnValue += `Mindre end ${values[0]} dage siden`;
      break;
    case "LessThanWeeksAgo":
      returnValue += `Mindre end ${values[0]} uger siden`;
      break;
    case "LessThanMonthsAgo":
      returnValue += `Mindre end ${values[0]} måneder siden`;
      break;
    case "LessThanYearsAgo":
      returnValue += `Mindre end ${values[0]} år siden`;
      break;
    case "Between":
      returnValue += `Mellem ${formatDate(values[0])} og ${formatDate(
        values[1],
      )}`;
      break;
    case "BetweenDaysAgo":
      returnValue += `Mellem ${values[0]} og ${values[1]} dage siden`;
      break;
    case "BetweenWeeksAgo":
      returnValue += `Mellem ${values[0]} og ${values[1]} uger siden`;
      break;
    case "BetweenMonthsAgo":
      returnValue += `Mellem ${values[0]} og ${values[1]} måneder siden`;
      break;
    case "BetweenYearsAgo":
      returnValue += `Mellem ${values[0]} og ${values[1]} år siden`;
      break;
    case "After":
      returnValue += `Efter ${formatDate(values[0])}`;
      break;
    case "Before":
      returnValue += `Før ${formatDate(values[0])}`;
      break;
    default:
      throw new Error(
        `Unsupported operator: ${operator} for BasicInfo.CreationDate filter`,
      );
  }
  return returnValue;
}
export function buildFilterGroupFromLabelInfo(labels: LabelInfo[]) {
  return mapAndExclude(labels, (exclude, labelInfo) => {
    try {
      getFilterGroup(`hasAiLabel_${labelInfo.labelId}`, false);
      return exclude;
    } catch {
      /* empty */
    }

    return createFilterGroup({
      id: labelInfo.labelId,
      title: labelInfo.prettyName,
      filterType: filterGroupTypes.label,
      yesLabel: `Har "${labelInfo.prettyName}"`,
      noLabel: `Har ikke "${labelInfo.prettyName}"`,
      keywords: [],
      filters: {
        value: {
          fieldName: "Labels.Labels",
          filterName: `hasAiLabel_${labelInfo.labelId}`,
        },
      },
      values: [labelInfo.labelId],
      description: labelInfo.description ?? undefined,
      DisplayValue: ({ filters }) => {
        const labelFilter = getFilterByFilterName(
          `hasAiLabel_${labelInfo.labelId}`,
          filters,
        );
        return labelFilter?.operator === "HasLabel" ? "Ja" : "Nej";
      },
    });
  });
}
